import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useConfigStore from '../../store/useConfigStore';
import { useDesktopStatus } from '../../hooks/useDesktopStatus';

const ChevronDownIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>;
const CheckIcon = ({ className }) => <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>;

const PROVIDER_ICONS = {
    gemini: { icon: '✦', color: 'text-blue-400', bg: 'bg-blue-500/10' },
    anthropic: { icon: '◈', color: 'text-orange-400', bg: 'bg-orange-500/10' },
    openai: { icon: '◉', color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
    ollama: { icon: '🦙', color: 'text-purple-400', bg: 'bg-purple-500/10' },
};

const ModelSelector = ({ selectedConfigId, onSelect, className = "" }) => {
    const { aiConfigs, subscribeToConfigs } = useConfigStore();
    const { isOnline } = useDesktopStatus();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const unsub = subscribeToConfigs();
        return () => unsub && unsub();
    }, [subscribeToConfigs]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const configs = Object.values(aiConfigs?.configs || {});

    const selectedConfig = aiConfigs?.configs[selectedConfigId] ||
        aiConfigs?.configs[aiConfigs.defaultConfigId] ||
        configs[0];

    const iconDef = PROVIDER_ICONS[selectedConfig?.provider] || PROVIDER_ICONS.gemini;
    const displayName = selectedConfig?.name || 'Select Model';
    const isMainOllamaOffline = selectedConfig?.provider === 'ollama' && !isOnline;

    return (
        <div className={`relative ${className}`} ref={containerRef}>
            <motion.button
                type="button"
                whileHover={{ backgroundColor: 'rgba(155, 155, 155, 0.1)' }}
                whileTap={{ backgroundColor: 'rgba(155, 155, 155, 0.2)' }}
                transition={{ duration: 0.1 }}
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-transparent group"
            >
                <div className={`w-5 h-5 rounded-full ${iconDef.bg} flex items-center justify-center text-[10px] font-bold ${iconDef.color} shrink-0 relative`}>
                    {iconDef.icon}
                    {selectedConfig?.provider === 'ollama' && (
                        <div className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full border border-[var(--bg-main)] ${isOnline ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' : 'bg-[var(--text-dim)]'}`} />
                    )}
                </div>
                <span className={`text-xs font-medium truncate max-w-[100px] ${isMainOllamaOffline ? 'text-[var(--text-dim)]' : 'text-[var(--text-main)]'}`}>
                    {displayName}
                </span>
                <ChevronDownIcon className={`w-3.5 h-3.5 text-[var(--text-muted)] group-hover:text-[var(--text-main)] transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
            </motion.button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                        className="absolute bottom-full mb-2 left-0 w-64 bg-[var(--bg-secondary)]/90 backdrop-blur-xl border border-[var(--border-accent)] rounded-2xl shadow-2xl overflow-hidden z-[999] p-1.5"
                    >
                        <div className="px-3 py-2 border-b border-[var(--border-subtle)] mb-1">
                            <span className="text-[10px] font-bold text-[var(--text-muted)] uppercase tracking-widest text-opacity-70">Available Models</span>
                        </div>
                        <div className="max-h-60 overflow-y-auto custom-scrollbar">
                            {/* Cloud Configs */}
                            {configs.map((config) => {
                                const isSelected = config.id === selectedConfig?.id;
                                const cIcon = PROVIDER_ICONS[config.provider] || PROVIDER_ICONS.gemini;
                                const isOllama = config.provider === 'ollama';
                                const isOllamaOffline = isOllama && !isOnline;

                                return (
                                    <motion.button
                                        key={config.id}
                                        type="button"
                                        disabled={isOllamaOffline}
                                        whileHover={!isOllamaOffline ? { backgroundColor: 'rgba(155, 155, 155, 0.15)' } : {}}
                                        whileTap={!isOllamaOffline ? { backgroundColor: 'rgba(155, 155, 155, 0.25)' } : {}}
                                        transition={{ duration: 0.1 }}
                                        onClick={() => {
                                            onSelect(config.id);
                                            setIsOpen(false);
                                        }}
                                        className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-left group mb-0.5 transition-all ${isSelected ? 'bg-[var(--accent-primary)]/10' : ''} ${isOllamaOffline ? 'opacity-40 cursor-not-allowed grayscale-[0.5]' : ''}`}
                                    >
                                        <div className={`w-7 h-7 rounded-lg ${cIcon.bg} flex items-center justify-center text-xs font-bold ${cIcon.color} shrink-0 relative`}>
                                            {cIcon.icon}
                                            {isOllama && (
                                                <div className={`absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full border-2 border-[var(--bg-secondary)] ${isOnline ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' : 'bg-[var(--text-dim)]'}`} />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <div className="text-[13px] font-medium text-[var(--text-main)] truncate">{config.name}</div>
                                                {isOllamaOffline && <span className="text-[9px] font-bold text-red-500/80 uppercase tracking-tighter">Offline</span>}
                                            </div>
                                            <div className="text-[10px] text-[var(--text-muted)] truncate">{config.model}</div>
                                        </div>
                                        {isSelected && <CheckIcon className="w-4 h-4 text-[var(--accent-primary)]" />}
                                    </motion.button>
                                );
                            })}

                            {configs.length === 0 && (
                                <div className="p-4 text-center text-xs text-[var(--text-muted)]">
                                    No models configured. <br /> Check Settings.
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default ModelSelector;
